import React, { Suspense, useContext } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { getRoutes } from './utils/route';
import { useStateValue } from './context/AppContext';
import { ModalDispatchContext } from './components/ui/modal/ModalContext';
import { isPreview } from './utils/common';

// non-digital assessments
import BulkOperations from './components/nonDigital/students/bulk/Container';
import ErrorPage from './components/nonDigital/error/ErrorPage';
import Help from './components/common/help/Help';
import Home from './components/nonDigital/landing/Home';
import IrregularityCategories from './components/common/irregularities/categories/Container';
import IrregularityFormContainer from './components/common/irregularities/form/Container';
import IrregularityRoster from './components/common/irregularities/roster/Container';
import RoomDetails from './components/nonDigital/rooms/details/Container';
import RoomForm from './components/common/rooms/form/Container';
import Rooms from './components/nonDigital/rooms/roster/Container';
import StaffDetails from './components/nonDigital/staff/details/Container';
import StaffForm from './components/nonDigital/staff/form/Container';
import StaffRoster from './components/nonDigital/staff/roster/Container';
import StudentDetails from './components/nonDigital/students/details/Container';
import Students from './components/nonDigital/students/roster/Container';
import TestDayRoster from './components/digital/testDayLive/roster/Container';

import VoucherInfo from './components/nonDigital/voucherInfo/Container';

// digital assessments
const DigitalBulkOperations = React.lazy(() => import('./components/digital/students/bulk/Container'));
const DigitalErrorPage = React.lazy(() => import('./components/digital/error/ErrorPage'));
const GroupsInfo = React.lazy(() => import('./components/common/groupsInfo/Container'));
const DigitalHome = React.lazy(() => import('./components/digital/landing/Home'));
const DigitalRooms = React.lazy(() => import('./components/digital/weekend/rooms/roster/Container'));
const DigitalRoomDetails = React.lazy(() => import('./components/digital/weekend/rooms/details/Container'));
const DigitalStudentDetails = React.lazy(() => import('./components/digital/students/details/Container'));
const DigitalStudents = React.lazy(() => import('./components/digital/students/roster/Container'));
const Downloads = React.lazy(() => import('./components/digital/downloads/Container'));
const Vouchers = React.lazy(() => import('./components/digital/vouchers/Container'));

// digital school based assessments
import DigitalInSchoolRoomDetails from './components/digital/inSchool/rooms/details/Container';

import Spinner from './components/ui/loading/SpinnerWrapper';
import SocketProvider from './components/common/network/SocketProvider';
import { useAnalyticsPageChange } from './utils/analytics';

type Route = {
  // eslint-disable-next-line
  component: any;
  path: string;
  localNav?: boolean;
  multiline?: boolean;
  title?: string;
  weight?: number;
  disabled?: boolean;
};

// eslint-disable-next-line
function authorizedRoutes(orgEvent: any, user: any) {
  const previewMode = isPreview(user?.role, user?.mode, user?.active);
  const routes: Record<string, Route[]> = {};
  routes.elevated = [
    {
      component: <StaffRoster />,
      localNav: true,
      path: '/staff',
      title: 'Staff',
      weight: 2,
    },
    {
      component: <RoomForm />,
      path: '/rooms/edit/:id',
    },
    {
      component: <StaffForm />,
      path: '/staff/add',
    },
    {
      component: <StaffDetails />,
      path: '/staff/get/:id',
    },
    {
      component: <StaffForm />,
      path: '/staff/edit/:id',
    },
    {
      component: <VoucherInfo />,
      localNav: true,
      path: '/vouchers',
      title: 'Voucher Data',
      weight: 6,
    },
  ];

  routes.digitalElevated = [
    {
      component: <StaffRoster />,
      localNav: true,
      path: '/staff',
      title: 'Staff',
      weight: 2,
    },
    {
      component: <TestDayRoster />,
      path: '/livedata', // (ref: livedata_url_path)
      title: 'Test Day Live',
    },
    {
      component: <RoomForm />,
      path: '/rooms/edit/:id',
    },
    {
      component: <StaffForm />,
      path: '/staff/add',
    },
    {
      component: <StaffDetails />,
      path: '/staff/get/:id',
    },
    {
      component: <StaffForm />,
      path: '/staff/edit/:id',
    },
  ];

  routes.digitalInSchoolHomeWithRoom = [
    {
      component: <DigitalInSchoolRoomDetails />,
      localNav: true,
      path: '/',
      title: 'My Room',
      weight: 0,
    },
  ];

  routes.DigitalHomeWithRoom = [
    {
      component: <DigitalRoomDetails />,
      localNav: true,
      path: '/',
      title: 'My Room',
      weight: 0,
    },
  ];

  routes.digitalHome = [
    {
      component: <DigitalHome />,
      localNav: true,
      path: '/',
      title: 'Home',
      weight: 0,
    },
  ];

  routes.digitalIrregularities = [
    {
      component: <IrregularityRoster />,
      localNav: true,
      path: '/irregularities',
      title: 'Irregularities',
      weight: 7,
    },
    {
      component: <IrregularityCategories />,
      path: '/irregularities/add',
    },
    {
      component: <IrregularityCategories />,
      path: '/irregularities/add/:irregularityCategoryId/:irregularityId',
    },
    {
      component: <IrregularityFormContainer />,
      path: '/irregularities/get/:id',
    },
  ];

  // TODO: AOPS-59585: remove Vouchers code reference part of clean up
  routes.digitalVouchers = [
    {
      component: <Vouchers />,
      localNav: true,
      path: '/digital-vouchers',
      title: 'Vouchers',
      weight: 8,
    },
  ];

  routes.digitalInSchool = [
    {
      component: <RoomForm />,
      localNav: false,
      path: '/rooms/edit/:id',
    },
    {
      component: <DigitalStudentDetails />,
      path: '/students/get/:id',
    },
    {
      component: <DigitalBulkOperations />,
      path: '/students/bulkOperations/:operation',
    },
    {
      component: <DigitalRooms />,
      localNav: true,
      path: '/rooms',
      title: 'All Rooms',
      weight: 3,
      disabled: previewMode,
    },
    {
      component: <DigitalStudents />,
      localNav: true,
      path: '/students',
      title: 'All Students',
      weight: 4,
      disabled: previewMode,
    },
  ];

  routes.digitalInSchoolRoomDetails = [
    {
      component: <DigitalInSchoolRoomDetails />,
      path: '/rooms/get/:id',
    },
    {
      component: <DigitalInSchoolRoomDetails />,
      path: '/rooms/get/:id/flow/:flowName/step/:stepNum',
      title: 'My Room',
    },
  ];

  routes.digitalHelp = [
    {
      component: <Help />,
      localNav: true,
      path: '/help',
      title: 'Help',
      weight: 2,
    },
  ];

  routes.weekendDigitalRoomDetails = [
    {
      component: <DigitalRoomDetails />,
      path: '/rooms/get/:id',
    },
    {
      component: <DigitalRoomDetails />,
      path: '/rooms/get/:id/flow/:flowName/step/:stepNum',
      title: 'My Room',
    },
  ];

  routes.digitalCommon = [
    {
      component: (
        <div className='container'>
          <GroupsInfo />
        </div>
      ),
      path: '/groups',
    },
    {
      component: <DigitalErrorPage />,
      path: '/error',
    },
    {
      component: <DigitalRooms />,
      localNav: true,
      path: '/rooms',
      title: 'All Rooms',
      weight: 2,
      disabled: previewMode,
    },
    {
      component: <DigitalStudents />,
      localNav: true,
      path: '/students',
      title: 'All Students',
      weight: 3,
      disabled: previewMode,
    },
    {
      component: <DigitalStudentDetails />,
      path: '/students/get/:id',
    },
    {
      component: <DigitalBulkOperations />,
      path: '/students/bulkOperations/:operation',
    },
  ];

  routes.irregularities = [
    {
      component: <IrregularityRoster />,
      localNav: true,
      path: '/irregularities',
      title: 'Irregularities',
      weight: 5,
    },
    {
      component: <IrregularityCategories />,
      path: '/irregularities/add',
    },
    {
      component: <IrregularityCategories />,
      path: '/irregularities/add/:irregularityCategoryId/:irregularityId',
    },
    {
      component: <IrregularityFormContainer />,
      path: '/irregularities/get/:id',
    },
  ];

  routes.common = [
    {
      // Show the user's room if this is a digital test and they are NOT an admin.
      component: <Home />,
      localNav: true,
      path: '/',
      title: 'Home',
      weight: 0,
    },
    {
      component: <Rooms />,
      localNav: true,
      path: '/rooms',
      title: 'Rooms',
      weight: 1,
    },
    {
      component: <RoomDetails />,
      path: '/rooms/get/:id',
    },
    {
      component: <Students />,
      localNav: true,
      path: '/students',
      title: 'Students',
      weight: 3,
    },
    {
      component: <StudentDetails />,
      path: '/students/get/:id',
    },
    {
      component: <BulkOperations />,
      path: '/students/bulkOperations/:operation',
    },
    {
      component: <Help />,
      localNav: true,
      path: '/help',
      title: 'Help',
      weight: 7,
    },
    {
      component: <GroupsInfo />,
      path: '/groups',
    },
    {
      component: <ErrorPage />,
      path: '/error',
    },
  ];

  routes.downloads = [
    {
      component: (
        <SocketProvider>
          <Downloads />
        </SocketProvider>
      ),
      localNav: true,
      path: '/downloads',
      title: 'Downloads',
      weight: 6,
    },
  ];

  return getRoutes(orgEvent, user).reduce((prev, cur) => {
    if (routes[cur]) {
      return [...prev, ...routes[cur]];
    } else {
      return [...prev];
    }
  }, [] as Route[]);
}

function AppRouter() {
  // Global App state.
  const { orgEvent, user } = useStateValue();
  const { pathname, hash } = useLocation();

  const dispatchModal = useContext(ModalDispatchContext);

  /**
   * Close Modal and scroll to the top on a page refresh unless they have a specific hash to jump to.
   */
  React.useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
    }
    dispatchModal(null);
  }, [hash, pathname, dispatchModal]);

  useAnalyticsPageChange(pathname);

  const routes = authorizedRoutes(orgEvent, user).filter((route) => !route.disabled);
  return (
    <Routes>
      {routes.map((routeObj) => (
        <Route
          key={routeObj.path}
          path={routeObj.path}
          element={<Suspense fallback={<Spinner />}>{routeObj.component}</Suspense>}
        />
      ))}
      {/** 404 Catch All */}
      <Route element={<ErrorPage errorCode='404' />} />
    </Routes>
  );
}

// eslint-disable-next-line
export { authorizedRoutes };

export default AppRouter;
